import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import useSocket from '../hooks/useSocket';
import { buildApiUrl } from '../config/api';
import logger from '../utils/logger';
import { candidaturesApi } from '../utils/candidaturesApi';
import { questionnairesApi } from '../utils/questionnairesApi';

const NavigationContext = createContext();
const NO_UNREAD = { unread: 0, byStudent: {} };

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its provider (established convention; dev-only HMR hint)
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    return { registerCallback: () => {}, unregisterCallback: () => {}, triggerCallback: () => {}, unreadCount: 0, refreshUnreadCount: () => {}, newProspectsCount: 0, refreshProspectsCount: () => {}, unreadResponses: NO_UNREAD, refreshUnreadResponses: () => {} };
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [navigationCallbacks, setNavigationCallbacks] = useState({});
  const { user, getAuthToken } = useAuth();
  const { socket } = useSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  // Candidatures still « Nouveau »: dot on Questionnaires and its Candidature tab.
  const [newProspectsCount, setNewProspectsCount] = useState(0);
  const canSeeProspects = user?.role === 'coach' && user?.features?.questionnaires !== false;
  // Students' answers not opened yet (onboarding + weekly): indicator on the
  // student in the Clients lists, dot on their « Bilans » tab.
  const [unreadResponses, setUnreadResponses] = useState(NO_UNREAD);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const token = await getAuthToken();
      const response = await fetch(buildApiUrl('/api/chat/conversations'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        const conversations = data.data || [];
        const total = conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
        setUnreadCount(total);
      }
    } catch (error) {
      logger.error('Error fetching unread count:', error);
    }
  }, [user, getAuthToken]);

  useEffect(() => {
    refreshUnreadCount();
    if (socket) {
      // `new_message` ne part qu'aux écrans qui ont la conversation ouverte ;
      // `new_message_notification` part au destinataire où qu'il soit dans
      // l'app : c'est lui qui fait monter la pastille. `messages_read` arrive
      // aussi sur tous les écrans du lecteur : la pastille redescend.
      socket.on('new_message', refreshUnreadCount);
      socket.on('new_message_notification', refreshUnreadCount);
      socket.on('messages_read', refreshUnreadCount);
      return () => {
        socket.off('new_message', refreshUnreadCount);
        socket.off('new_message_notification', refreshUnreadCount);
        socket.off('messages_read', refreshUnreadCount);
      };
    }
  }, [user, socket, refreshUnreadCount]);

  // One count request (head only) at start, then on each new candidature.
  const refreshProspectsCount = useCallback(async () => {
    if (!canSeeProspects) { setNewProspectsCount(0); return; }
    try {
      const summary = await candidaturesApi.summary();
      setNewProspectsCount(summary?.newCount || 0);
    } catch (error) {
      logger.error('Error fetching new prospects count:', error);
    }
  }, [canSeeProspects]);

  useEffect(() => {
    refreshProspectsCount();
    if (!socket || !canSeeProspects) return undefined;
    const onCoachNotification = (payload) => {
      if (payload?.notification?.type === 'candidature_received') refreshProspectsCount();
    };
    socket.on('coach_notification', onCoachNotification);
    return () => socket.off('coach_notification', onCoachNotification);
  }, [socket, canSeeProspects, refreshProspectsCount]);

  const refreshUnreadResponses = useCallback(async () => {
    if (!canSeeProspects) { setUnreadResponses(NO_UNREAD); return; }
    try {
      const summary = await questionnairesApi.responsesSummary();
      setUnreadResponses({ unread: summary?.unread || 0, byStudent: summary?.byStudent || {} });
    } catch (error) {
      logger.error('Error fetching unread questionnaire answers:', error);
    }
  }, [canSeeProspects]);

  // At start, then on each answer sent by a student (live).
  useEffect(() => {
    refreshUnreadResponses();
    if (!socket || !canSeeProspects) return undefined;
    const onSubmitted = (payload) => {
      if (payload?.notification?.type === 'questionnaire_submitted') refreshUnreadResponses();
    };
    socket.on('coach_notification', onSubmitted);
    return () => socket.off('coach_notification', onSubmitted);
  }, [socket, canSeeProspects, refreshUnreadResponses]);

  const registerCallback = (key, callback) => {
    setNavigationCallbacks(prev => ({
      ...prev,
      [key]: callback
    }));
  };

  const unregisterCallback = (key) => {
    setNavigationCallbacks(prev => {
      const newCallbacks = { ...prev };
      delete newCallbacks[key];
      return newCallbacks;
    });
  };

  const triggerCallback = (key) => {
    if (navigationCallbacks[key]) {
      navigationCallbacks[key]();
    }
  };

  return (
    <NavigationContext.Provider value={{
      registerCallback,
      unregisterCallback,
      triggerCallback,
      unreadCount,
      refreshUnreadCount,
      newProspectsCount,
      refreshProspectsCount,
      unreadResponses,
      refreshUnreadResponses
    }}>
      {children}
    </NavigationContext.Provider>
  );
};
